/**
 * InternalTreatmentForm — editor for treatments fulfilled from clinic stock.
 *
 * Covers both API types: `internal` (take-home, pending order) and
 * `internal_administered` (dispensed in consult, immediate stock deduction).
 *
 * Handles medications and consumables. Vaccines and services have their own
 * dedicated forms (`VaccineTreatmentForm` / `ServiceTreatmentForm`).
 *
 * The schema split follows the API contract, not the product type:
 * - `internalSchema` validates take-home items (instructions required, batch optional)
 * - `internalAdministeredSchema` validates items dispensed in consult (batch required,
 *   instructions optional)
 *
 * The dispense method (in-consult vs take-home) is resolved by
 * `resolveAdministrationMethod` based on the selected product type and
 * prescribing context.
 */
import { useStore } from '@tanstack/react-form'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { z } from 'zod'

import type {
  ClinicId,
  PrescribingContext,
  Product,
  ProductType,
  RefillInterval,
} from '@snootsvet/shared'
import {
  calculateOrderTotal,
  clinicIdSchema,
  formatCalendarDate,
  formatRefillInterval,
  formatRegionPrice,
  getClinic,
  pluralizeUnit,
  refillIntervalSchema,
} from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { FormError } from '@/frontend/components/form/primitives'
import { zodErrorToFieldErrors } from '@/frontend/components/form/validation'
import { useViewerFormat } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { ProductWithStockSearch } from '@/frontend/features/inventory/product-search'
import {
  AllocationProgressBadge,
  type EnrichedAllocationItem,
  StockAllocationBatchSummary,
  StockAllocationResetButton,
  validateStockAllocations,
} from '@/frontend/features/orders/stock-allocation.utils'
import { StockAllocationEditor } from '@/frontend/features/orders/stock-allocation-editor'
import { StockWarningRemote } from '@/frontend/features/orders/stock-warning'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useGetPatientSubscription } from '@/frontend/hooks/api/patients/use-get-patient-subscription'
import { useGetProduct } from '@/frontend/hooks/api/use-get-product'
import { calculateDefaultPrescriptionExpiry } from '@/frontend/lib/treatments'

import { RefillIntervalPicker } from './refill-interval-picker'
import type { InternalFormItem } from './treatments.types'
import { useFefoAllocation } from './use-fefo-allocation'

// ---------------------------------------------------------------------------
// Form values & schemas
// ---------------------------------------------------------------------------

/**
 * Resolves the default value of `administeredInConsult` and whether the user
 * can toggle it, based on product type and prescribing context.
 *
 * - Standalone/remote: always false (can't administer outside a consultation)
 * - Vaccines and services: always true, locked
 * - Consumables in clinic: default true, user can switch to take-home
 * - Medications in clinic: default false, user can switch to in-consult
 */
function resolveAdministrationMethod(
  context: PrescribingContext,
  product?: Pick<Product, 'type'> | null
): { administeredInConsult: boolean; canEdit: boolean } {
  if (context !== 'clinic_consultation') {
    return { administeredInConsult: false, canEdit: false }
  }
  if (product?.type === 'vaccine' || product?.type === 'service') {
    return { administeredInConsult: true, canEdit: false }
  }
  if (product?.type === 'consumable') {
    return { administeredInConsult: true, canEdit: true }
  }
  return { administeredInConsult: false, canEdit: true }
}

/** Pre-validation form state — all fields nullable for empty-form state. */
interface InternalFormValues {
  productId: string | null
  quantity: number | null
  clinicId: ClinicId | null
  instructions: string | null
  repeatsAllowed: number | null
  validUntil: Date | null
  refillInterval: RefillInterval | null
  urgency: 'low' | 'medium' | 'high'
  zeroFee: boolean
  administeredInConsult: boolean
  allocations: EnrichedAllocationItem[]
  clinicalNotes: string | null
  /** Operational/dispensing notes — editable in standalone context only. */
  internalNotes: string | null
  /** Physical vaccine tag number — only shown for vaccine products. */
  tagNumber: string | null
  /** Free-text injection site — only shown for vaccine products. */
  injectionSite: string | null
}

/** Maps a validated item back to pre-validation form state for editing. */
function toFormValues(item: InternalFormItem, prescribedAt: Date): InternalFormValues {
  return {
    productId: item.productId,
    quantity: item.quantity,
    clinicId: item.clinicId,
    zeroFee: item.zeroFee,
    clinicalNotes: item.clinicalNotes,
    internalNotes: item.internalNotes,
    tagNumber: item.tagNumber,
    injectionSite: item.injectionSite,
    administeredInConsult: item.type === 'internal_administered',
    allocations: item.type === 'internal_administered' ? item.allocations : [],
    instructions: item.type === 'internal' ? item.instructions : null,
    repeatsAllowed: item.type === 'internal' ? item.repeatsAllowed : null,
    refillInterval: item.type === 'internal' ? item.refillInterval : null,
    validUntil:
      item.type === 'internal'
        ? (item.validUntil ??
          (item.repeatsAllowed > 0 && item.refillInterval
            ? calculateDefaultPrescriptionExpiry(
                prescribedAt,
                item.repeatsAllowed,
                item.refillInterval
              )
            : null))
        : null,
    urgency: item.type === 'internal' ? item.urgency : 'medium',
  }
}

const sharedFields = {
  productId: z.string({ message: 'Please select a product' }).min(1, 'Please select a product'),
  quantity: z
    .number({ message: 'Please enter a quantity' })
    .positive('Quantity must be greater than 0')
    .multipleOf(0.01, 'Maximum 2 decimal places'),
  clinicId: clinicIdSchema,
  urgency: z.enum(['low', 'medium', 'high']),
  zeroFee: z.boolean(),
  clinicalNotes: z.string().nullable(),
  internalNotes: z.string().nullable(),
  tagNumber: z.string().nullable(),
  injectionSite: z.string().nullable(),
}

/** Take-home item — instructions required, batch optional. */
const internalSchema = z
  .object({
    ...sharedFields,
    instructions: z
      .string({ message: 'Instructions are required' })
      .min(1, 'Instructions are required'),
    repeatsAllowed: z.number().int().min(0),
    refillInterval: refillIntervalSchema.nullable(),
    validUntil: z.date().nullable(),
    administeredInConsult: z.literal(false),
  })
  .superRefine((value, ctx) => {
    if (value.repeatsAllowed === 0) return
    if (!value.refillInterval) {
      ctx.addIssue({
        code: 'custom',
        path: ['refillInterval'],
        message: 'Please specify a refill interval',
      })
    }
  })
  .refine((value) => value.repeatsAllowed === 0 || value.validUntil !== null, {
    path: ['validUntil'],
    message: 'Please specify when this prescription expires',
  })
  .transform((value) => ({
    ...value,
    refillInterval: value.repeatsAllowed > 0 ? value.refillInterval : null,
    validUntil: value.repeatsAllowed > 0 ? value.validUntil : null,
  }))

/** Administered in consult — batch required for batch-tracked products, optional otherwise. */
const internalAdministeredSchema = z
  .object({
    ...sharedFields,
    administeredInConsult: z.literal(true),
    allocations: z.array(
      z.object({
        batchNumber: z.string().nullable(),
        quantity: z.number().min(0),
      })
    ),
  })
  .superRefine((v, ctx) => validateStockAllocations(v.allocations, v.quantity, ctx))
  .transform((v) => ({
    ...v,
    allocations: v.allocations.filter((a) => a.quantity > 0),
  }))

function AdministeredInConsultConfirmation({
  product,
  quantity,
  allocations,
  discountBps,
  totalPrice,
  priceRegion,
  unitPrice,
}: {
  product: Pick<Product, 'name' | 'unit'> | null
  quantity: number | null
  allocations: EnrichedAllocationItem[]
  discountBps: number
  totalPrice: number | null
  priceRegion: Product['region']
  unitPrice: number
}) {
  return (
    <div className="flex flex-col gap-2">
      <p>
        This treatment will be administered in consult and stock will be deducted immediately. It
        can be edited or deleted within 7 days while unpaid.
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4">
        <dt className="font-medium text-foreground">Product:</dt>
        <dd>
          {product?.name ?? 'Unknown'} — {quantity ?? 0} ×{' '}
          {pluralizeUnit(product?.unit ?? null, quantity ?? 0)}
        </dd>
        {allocations.length > 0 && (
          <>
            <dt className="font-medium text-foreground">Batch:</dt>
            <dd>
              {allocations
                .map((a) => `${a.batchNumber ?? 'No batch number'} × ${a.quantity}`)
                .join(', ')}
            </dd>
          </>
        )}
        {discountBps > 0 && (
          <>
            <dt className="font-medium text-foreground">Discount:</dt>
            <dd>{discountBps / 100}%</dd>
          </>
        )}
        <dt className="font-medium text-foreground">Charge:</dt>
        <dd>
          {formatRegionPrice(totalPrice ?? 0, priceRegion)}
          {totalPrice !== 0 && unitPrice > 0 && (
            <>
              {' '}
              ({formatRegionPrice(unitPrice, priceRegion)} per{' '}
              {pluralizeUnit(product?.unit ?? null, 1)})
            </>
          )}
        </dd>
      </dl>
    </div>
  )
}

function RefillConfirmation({
  quantity,
  productUnit,
  repeatsAllowed,
  refillInterval,
  validUntil,
}: {
  quantity: number | null
  productUnit: string | null
  repeatsAllowed: number
  refillInterval: RefillInterval | null
  validUntil: Date | null
}) {
  const viewerDateFormat = useViewerFormat()
  const quantityLabel =
    quantity != null && productUnit
      ? `${quantity} ${pluralizeUnit(productUnit, quantity)}`
      : 'the prescribed quantity'

  return (
    <div className="flex flex-col gap-3">
      <p>
        Please confirm that the dose and quantity are appropriate for the initial instalment and
        each refill.
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <dt className="font-medium text-foreground">Initial instalment</dt>
        <dd>{quantityLabel}</dd>
        <dt className="font-medium text-foreground">Refills</dt>
        <dd>
          {repeatsAllowed} further {repeatsAllowed === 1 ? 'instalments' : 'instalments'} of{' '}
          {quantityLabel}
        </dd>
        {refillInterval && (
          <>
            <dt className="font-medium text-foreground">Refill interval</dt>
            <dd>Every {formatRefillInterval(refillInterval)}</dd>
          </>
        )}
        {validUntil && (
          <>
            <dt className="font-medium text-foreground">Prescription valid until</dt>
            <dd>{formatCalendarDate(validUntil, viewerDateFormat)}</dd>
          </>
        )}
      </dl>
      <p>Any refills not requested by the expiry date will no longer be available.</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface InternalTreatmentFormProps {
  patientId?: string
  defaultValues?: InternalFormItem & { createdAt?: string }
  /** Prescribing context - determines available product types and dispense behaviour. */
  prescribingContext: PrescribingContext
  /**
   * Clinic context in which the form is mounted (e.g. the consultation's clinic).
   * When set, the clinic is fixed — no clinic picker is shown.
   * Distinct from `defaultValues.clinicId`, which may come from a
   * previous treatment being re-prescribed.
   */
  contextClinicId?: ClinicId
  onSave: (item: InternalFormItem) => void
  onCancel: () => void
  editing?: boolean
  /** Whether an async save is in progress — disables submit button. */
  saving?: boolean
}

export function InternalTreatmentForm({
  patientId,
  defaultValues,
  prescribingContext,
  contextClinicId,
  onSave,
  onCancel,
  editing = false,
  saving = false,
}: InternalTreatmentFormProps) {
  const isStandalone = prescribingContext === 'standalone'

  const initialAdministrationMethod = resolveAdministrationMethod(
    prescribingContext,
    defaultValues?.product
  )
  const [canEditAdministeredInConsult, setCanEditAdministeredInConsult] = useState(
    initialAdministrationMethod.canEdit
  )

  const [prescribedAt] = useState(() =>
    defaultValues?.createdAt ? new Date(defaultValues.createdAt) : new Date()
  )

  const form = useAppForm({
    defaultValues: defaultValues
      ? toFormValues(
          { ...defaultValues, ...(contextClinicId && { clinicId: contextClinicId }) },
          prescribedAt
        )
      : {
          productId: null,
          quantity: null,
          instructions: null,
          repeatsAllowed: 0,
          refillInterval: null as RefillInterval | null,
          validUntil: null as Date | null,
          urgency: 'medium' as const,
          zeroFee: false,
          allocations: [],
          clinicalNotes: null,
          internalNotes: null,
          tagNumber: null,
          injectionSite: null,
          clinicId: contextClinicId ?? null,
          administeredInConsult: initialAdministrationMethod.administeredInConsult,
        },
    validators: {
      onSubmit: ({ value }) => {
        const schema = value.administeredInConsult
          ? internalAdministeredSchema
          : internalSchema.superRefine((value, ctx) => {
              if (!product?.isControlledSubstance || value.repeatsAllowed === 0) return
              ctx.addIssue({
                code: 'custom',
                path: ['repeatsAllowed'],
                message: 'Controlled substances cannot have refills',
              })
            })
        const result = schema.safeParse(value)
        if (!result.success) return zodErrorToFieldErrors(result.error)
      },
    },
    onSubmit: ({ value }) => {
      // Re-parse to get the validated + transformed output (zero-quantity
      // allocations stripped). The validators.onSubmit pass already
      // guaranteed this succeeds.
      const schema = value.administeredInConsult ? internalAdministeredSchema : internalSchema
      const parsed = schema.parse(value)

      // internalNotes is editable in standalone context only; elsewhere the field
      // is hidden and the existing value (if any) is preserved via the defaults.
      if (parsed.administeredInConsult) {
        onSave({
          type: 'internal_administered',
          treatmentCategory: 'prescription',
          productId: parsed.productId,
          quantity: parsed.quantity,
          clinicId: parsed.clinicId,
          allocations: parsed.allocations,
          zeroFee: parsed.zeroFee,
          clinicalNotes: parsed.clinicalNotes,
          internalNotes: parsed.internalNotes,
          tagNumber: parsed.tagNumber,
          injectionSite: parsed.injectionSite,
          // Vaccines (with their reminders) have their own form; this form handles
          // medications and consumables, which never carry booster reminders.
          reminders: [],
          product: product ?? null,
        })
      } else {
        onSave({
          type: 'internal',
          treatmentCategory: 'prescription',
          productId: parsed.productId,
          quantity: parsed.quantity,
          clinicId: parsed.clinicId,
          instructions: parsed.instructions,
          repeatsAllowed: parsed.repeatsAllowed,
          refillInterval: parsed.refillInterval,
          validUntil: parsed.validUntil,
          urgency: parsed.urgency,
          zeroFee: parsed.zeroFee,
          clinicalNotes: parsed.clinicalNotes,
          internalNotes: parsed.internalNotes,
          tagNumber: parsed.tagNumber,
          injectionSite: parsed.injectionSite,
          product: product ?? null,
        })
      }
    },
  })

  const productId = useStore(form.store, (s) => s.values.productId)

  // Fetch product data by ID rather than relying on the ProductWithStockSearch onChange
  // to populate it. In edit mode the search is disabled so onChange never fires, leaving
  // stock data empty. Keying a query on productId unifies both flows — create and edit —
  // around a single fetch.
  const { data: product, ...productQuery } = useGetProduct(productId, { include: 'stock' })
  const { data: subscription, ...subscriptionQuery } = useGetPatientSubscription(patientId)
  const hasPlan =
    subscription !== undefined &&
    'addons' in subscription &&
    (subscription.addons.some((addon) => /flea|worm|tick/i.test(addon.name)) ||
      // Temporary bundled and standalone plan support; remove this branch after subscription repairs.
      subscription.hasFleaWormTickPlan)
  useEffect(() => {
    if (!editing && product?.fleaWormTick && hasPlan) form.setFieldValue('zeroFee', true)
  }, [editing, form, product?.fleaWormTick, hasPlan])

  /** Updates form fields that depend on the selected product. */
  const onProductChange = useCallback(
    (p: Pick<Product, 'type' | 'isControlledSubstance' | 'fleaWormTick'> | null) => {
      form.setFieldValue('quantity', 1)
      const nextAdministrationMethod = resolveAdministrationMethod(prescribingContext, p)
      form.setFieldValue('administeredInConsult', nextAdministrationMethod.administeredInConsult)
      setCanEditAdministeredInConsult(nextAdministrationMethod.canEdit)
      form.setFieldValue('allocations', [])
      if (p?.isControlledSubstance || p?.type === 'service' || p?.type === 'consumable') {
        form.setFieldValue('repeatsAllowed', 0)
        form.setFieldValue('refillInterval', null)
      }
      form.setFieldValue('zeroFee', p?.fleaWormTick === true && hasPlan)
    },
    [form, prescribingContext, hasPlan]
  )

  const { region } = useViewerRegion()

  // Reactive form state
  const isControlledSubstance = product?.isControlledSubstance === true
  const administeredInConsult = useStore(form.store, (s) => s.values.administeredInConsult)
  const quantity = useStore(form.store, (s) => s.values.quantity)
  const repeatsAllowed = useStore(form.store, (s) => s.values.repeatsAllowed)
  const refillInterval = useStore(form.store, (s) => s.values.refillInterval)
  const validUntil = useStore(form.store, (s) => s.values.validUntil)

  function updateExpiry(count: number | null, interval: RefillInterval | null) {
    if (count && count > 0 && interval && refillIntervalSchema.safeParse(interval).success) {
      form.setFieldValue(
        'validUntil',
        calculateDefaultPrescriptionExpiry(prescribedAt, count, interval)
      )
    } else {
      form.setFieldValue('validUntil', null)
    }
  }

  const zeroFee = useStore(form.store, (s) => s.values.zeroFee)
  const discountBps = zeroFee ? 10_000 : 0
  const allocations = useStore(form.store, (s) => s.values.allocations)
  const clinicId = useStore(form.store, (s) => s.values.clinicId)

  const stockData = useFefoAllocation({
    product,
    clinicId,
    productId,
    quantity,
    hasInitialAllocations:
      defaultValues?.type === 'internal_administered' &&
      (defaultValues.allocations?.length ?? 0) > 0,
    onSuggest: (allocations) => form.setFieldValue('allocations', allocations),
  })

  // Product types this form offers. Vaccines and services have their own dedicated forms.
  const allowedProductTypes: ProductType[] = ['medication', 'consumable']
  const unitPrice = product?.unitPrice ?? 0
  const totalPrice =
    product && quantity ? calculateOrderTotal({ unitPrice, quantity, discountBps }) : null
  // Prices are denominated in the product's region; fall back to the viewer's
  // region only in the brief window before the product has loaded.
  const priceRegion = product?.region ?? region
  const showStockAllocation = administeredInConsult && clinicId != null && quantity != null
  const hasRefills = !administeredInConsult && (repeatsAllowed ?? 0) > 0

  return (
    <form.AppForm>
      <div className="flex flex-col gap-3">
        {/* Clinic selector */}
        {!contextClinicId && (
          <form.AppField name="clinicId">
            {(field) => (
              <FieldWrapper
                field={field}
                label="Dispensing Clinic"
                required
                render={({ id, disabled }) => (
                  <ClinicSearchWithDefault
                    id={id}
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    disabled={disabled}
                  />
                )}
              />
            )}
          </form.AppField>
        )}

        {/* Product selector */}
        <form.AppField name="productId">
          {(field) => (
            <FieldWrapper
              field={field}
              label="Product"
              required
              render={({ id, disabled }) => (
                <ProductWithStockSearch
                  id={id}
                  value={product ?? null}
                  onChange={(p) => {
                    field.handleChange(p ? p.id : null)
                    onProductChange(p)
                  }}
                  type={allowedProductTypes}
                  clinicId={clinicId ?? undefined}
                  onBlur={field.handleBlur}
                  aria-invalid={field.state.meta.errors.length > 0}
                  disabled={disabled || editing}
                  placeholder="Search products..."
                />
              )}
            />
          )}
        </form.AppField>

        {/* Quantity + refills */}
        <div className={administeredInConsult ? '' : 'grid grid-cols-2 gap-4'}>
          <form.AppField name="quantity">
            {(field) => (
              <field.NumericInputField
                label="Quantity"
                placeholder={administeredInConsult ? 'e.g. 1' : 'e.g. 14'}
                required
              />
            )}
          </form.AppField>

          {!administeredInConsult && (
            <form.AppField
              name="repeatsAllowed"
              listeners={{
                onChange: ({ value }) => {
                  if ((value ?? 0) <= 0) {
                    form.setFieldValue('refillInterval', null)
                  } else if (!form.getFieldValue('refillInterval')) {
                    form.setFieldValue('refillInterval', { amount: 1, unit: 'months' })
                  }
                  updateExpiry(value, form.getFieldValue('refillInterval'))
                },
              }}
            >
              {(field) => (
                <field.NumericInputField
                  label="Refills"
                  emptyValue={0}
                  placeholder="0"
                  disabled={isControlledSubstance}
                />
              )}
            </form.AppField>
          )}
        </div>

        {!administeredInConsult && (repeatsAllowed ?? 0) > 0 && (
          <div className="space-y-3">
            <form.AppField name="refillInterval.amount">
              {(amountField) => (
                <form.AppField name="refillInterval.unit">
                  {(unitField) => (
                    <>
                      <FieldWrapper
                        field={amountField}
                        label="Refill interval"
                        description="How often further instalments can be requested."
                        required
                        render={({ id, disabled }) => (
                          <RefillIntervalPicker
                            id={id}
                            value={{
                              amount: amountField.state.value ?? 0,
                              unit: unitField.state.value ?? 'days',
                            }}
                            onChange={(value) => {
                              amountField.handleChange(value.amount)
                              unitField.handleChange(value.unit)
                              updateExpiry(repeatsAllowed, value)
                            }}
                            onBlur={() => {
                              amountField.handleBlur()
                              unitField.handleBlur()
                            }}
                            disabled={disabled}
                            invalid={
                              amountField.state.meta.errors.length > 0 ||
                              unitField.state.meta.errors.length > 0
                            }
                          />
                        )}
                      />
                      <FormError errors={unitField.state.meta.errors} />
                    </>
                  )}
                </form.AppField>
              )}
            </form.AppField>
            <form.AppField name="validUntil">
              {(field) => (
                <field.DatePickerField
                  label="Prescription valid until"
                  description="A default end date is automatically calculated based on the number of refills and their interval, but you can choose a different date. After this date, a new prescription is needed to request more medication."
                  required
                  presets={[{ months: 3 }, { months: 6 }]}
                />
              )}
            </form.AppField>
            {quantity != null && product && (
              <p className="text-sm text-muted-foreground">
                Each refill has the same quantity as the initial instalment. Please check that the
                dose and quantity are appropriate for both the initial instalment and each refill.
              </p>
            )}
          </div>
        )}

        {/* Pricing */}
        {unitPrice > 0 && product && totalPrice != null && (
          <p className="text-muted-foreground">
            {`${quantity} ${pluralizeUnit(product.unit, quantity ?? 0)} × ${formatRegionPrice(unitPrice, priceRegion)} per ${pluralizeUnit(product.unit, 1)} = `}
            {discountBps > 0 && (
              <>
                <s>
                  {formatRegionPrice(
                    calculateOrderTotal({ unitPrice, quantity: quantity ?? 0, discountBps: 0 }),
                    priceRegion
                  )}
                </s>{' '}
              </>
            )}
            <span className="font-semibold text-foreground">
              {formatRegionPrice(totalPrice, priceRegion)}
            </span>
            {discountBps > 0 && ` (${discountBps / 100}% discount)`}
          </p>
        )}

        {/* Zero fee */}
        <div className="flex items-center gap-2">
          <form.AppField name="zeroFee">
            {(field) => (
              <field.CheckboxField label="Zero fee" description="Waive the charge for this item" />
            )}
          </form.AppField>
          {product?.fleaWormTick && hasPlan && zeroFee && (
            <Badge variant="success" size="sm">
              {region === 'us' ? 'Flea & tick plan active' : 'Flea & worming plan active'}
            </Badge>
          )}
          {productId &&
            (productQuery.isLoading || (product?.fleaWormTick && subscriptionQuery.isLoading)) && (
              <Loader2 className="size-4 animate-spin" />
            )}
        </div>

        {/* Dispense method — always visible in clinic context, locked when product type forces it */}
        {prescribingContext === 'clinic_consultation' && (
          <form.AppField name="administeredInConsult">
            {(field) => (
              <field.CheckboxField
                label="Administered in consult"
                description="Deduct from stock immediately instead of sending to dispensing queue"
                disabled={editing || !canEditAdministeredInConsult}
              />
            )}
          </form.AppField>
        )}

        {/* Stock allocation */}
        {showStockAllocation && (
          <form.AppField name="allocations">
            {(field) => {
              const allocated = allocations.reduce((sum, a) => sum + a.quantity, 0)
              return (
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 font-medium">Stock Allocation</span>
                      <StockAllocationBatchSummary
                        allocations={allocations}
                        ready={stockData != null}
                        stockStatus={stockData?.stockStatus ?? null}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <AllocationProgressBadge allocated={allocated} required={quantity ?? 0} />
                      {stockData && (
                        <StockAllocationResetButton
                          onReset={() => field.setValue(stockData.allocations)}
                        />
                      )}
                    </div>
                  </div>
                  {field.state.meta.errors && <FormError errors={field.state.meta.errors} />}
                  <StockAllocationEditor
                    allocations={allocations}
                    onChange={field.setValue}
                    quantity={quantity}
                    availableBatches={stockData?.availableBatches ?? []}
                    stockStatus={stockData?.stockStatus ?? 'no-stock'}
                    clinicId={clinicId ?? undefined}
                    productName={product?.name}
                  />
                </div>
              )
            }}
          </form.AppField>
        )}

        {/* Instructions — hidden when administered in consult */}
        {!administeredInConsult && (
          <form.AppField name="instructions">
            {(field) => (
              <field.TextareaField
                label="Instructions"
                placeholder="e.g. Take 1 tablet twice daily with food for 7 days"
                required
              />
            )}
          </form.AppField>
        )}

        {/* Urgency */}
        {!contextClinicId && (
          <form.AppField name="urgency">
            {(field) => (
              <FieldWrapper
                field={field}
                label="Urgency"
                render={() => (
                  <div className="flex gap-1.5">
                    {(['low', 'medium', 'high'] as const).map((level) => (
                      <Button
                        key={level}
                        type="button"
                        variant={field.state.value === level ? 'default' : 'outline'}
                        onClick={() => field.handleChange(level)}
                      >
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </Button>
                    ))}
                  </div>
                )}
              />
            )}
          </form.AppField>
        )}

        {/* Clinical + internal notes — only for standalone prescriptions; in-consult
            treatments capture clinical reasoning in the consultation notes instead. */}
        {isStandalone && (
          <>
            <form.AppField name="clinicalNotes">
              {(field) => (
                <field.TextareaField
                  label="Clinical Notes"
                  description="Standalone treatments aren't part of a consultation. Use this field to optionally capture any clinical reasoning that would otherwise go in the consultation notes. Shown on the patient record."
                  placeholder="e.g. Diagnosis, rationale, patient context"
                  rows={2}
                />
              )}
            </form.AppField>

            <form.AppField name="internalNotes">
              {(field) => (
                <field.TextareaField
                  label="Internal notes"
                  description="These notes will not be visible to the member. Use them for operational or dispensing notes for Snoots staff only."
                  placeholder="e.g. Client wants to collect on Friday at 2:30pm"
                  rows={2}
                />
              )}
            </form.AppField>
          </>
        )}

        {/* Stock warning for remote dispensing */}
        {!administeredInConsult && clinicId && quantity != null && quantity > 0 && (
          <StockWarningRemote
            stockStatus={stockData?.stockStatus ?? null}
            clinicName={getClinic(clinicId)?.name}
          />
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <form.ConfirmSubmitButton
            type="button"
            disabled={saving || (administeredInConsult && stockData?.stockStatus === 'no-stock')}
            heading={hasRefills ? 'Confirm prescription with refills' : 'Confirm treatment'}
            description={
              hasRefills ? (
                <div className="flex flex-col gap-3">
                  <RefillConfirmation
                    quantity={quantity}
                    productUnit={product?.unit ?? null}
                    repeatsAllowed={repeatsAllowed ?? 0}
                    refillInterval={refillInterval}
                    validUntil={validUntil}
                  />
                </div>
              ) : (
                <AdministeredInConsultConfirmation
                  product={product ?? null}
                  quantity={quantity}
                  allocations={allocations}
                  discountBps={discountBps}
                  totalPrice={totalPrice}
                  priceRegion={priceRegion}
                  unitPrice={unitPrice}
                />
              )
            }
            confirmLabel={hasRefills ? 'Confirm prescription' : 'Save'}
            bypass={!administeredInConsult && !hasRefills}
          >
            {saving ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : null}
            Save
          </form.ConfirmSubmitButton>
        </div>
      </div>
    </form.AppForm>
  )
}
